import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { trackInitialPageView } from '@eventbrite/site-analytics';
import { History } from 'history';
import get from 'lodash/get';
import replace from 'lodash/replace';
import startsWith from 'lodash/startsWith';
import upperFirst from 'lodash/upperFirst';
import { Store } from 'redux';
import {
    BASE_URL,
    CAPACITY,
    CAPACITY_WAITLIST,
    CheckoutFormSection,
    CHECKOUT_FORM_SECTION_LABEL,
    GROUP_REG_JOIN_PATH,
    INVITATION_REQUIRED,
    PASSWORD_REQUIRED,
    PROTECTED_PATH,
    STATUS_PATH,
    TICKETS_FULL_SIZE_PATH,
    TICKET_LEVEL,
} from '../constants';
import { PaymentMethodType } from '../models/paymentMethods/enums';
import { isUserLoggedIn } from '../selectors/user';
import { CheckoutState } from '../state';
import { trackPageLoad } from './trackPageLoad';

export const getWaitlistLabel = (props: any) =>
    get(props, 'event.waitlist_settings.ticket_id', '') === CAPACITY_WAITLIST
        ? CAPACITY
        : TICKET_LEVEL;

export const getCheckoutStepFromPath = (path: string) => {
    const checkoutStep = replace(path, `${BASE_URL}/`, '');

    return checkoutStep;
};

// See https://analytics.google.com/analytics/web/#/a141520w171290p135375432/admin/custom-dimensions/
// For a list of custom dimensions and their corresponding index
export const DIMENSION_PARENT_URL = 'dimension39';
export const DIMENSION_EVENT_ID = 'dimension12';

/**
 * Sends a pageview hit to google analytics every time a new page inside the widget flow is loaded.
 */
export const initializeGAPageView = ({
    historyManagement,
    store,
    appProps,
}: {
    historyManagement: History;
    store: Store<CheckoutState>;
    appProps: any;
}) => {
    const {
        gaSettings: contextGASettings,
        widgetParentUrl,
        event: { id: eventId },
    } = appProps;
    let firstRun = true;
    let domain = 'organizerSite';
    let label: string | undefined = undefined;

    if (appProps.isEventbriteTLD) {
        domain = 'eventbrite';
    }

    store.dispatch(
        trackPageLoad({
            checkoutStep: 'app',
            label: domain,
        }),
    );

    if (get(appProps, 'request.params.w', '') !== '') {
        store.dispatch(
            trackPageLoad({
                checkoutStep: 'appWaitlistToken',
                label: domain,
            }),
        );
    }

    historyManagement.listen(({ pathname: page }: { pathname: string }) => {
        // Avoid double-tracking the initial page load that's already tracked on index.js
        if (firstRun && page === BASE_URL) {
            firstRun = false;
            return;
        }

        const checkoutStep = getCheckoutStepFromPath(page);
        const state = store.getState();

        // Waitlist label is tracked on all page loads for waitlist events, except for the specific
        // page loads in the logic below
        if (get(appProps, 'event.waitlist_settings.ticket_id')) {
            label = getWaitlistLabel(appProps);
        }

        if (startsWith(checkoutStep, STATUS_PATH)) {
            label = upperFirst(replace(checkoutStep, 'status/', ''));
        }

        if (startsWith(checkoutStep, PROTECTED_PATH)) {
            const privacySetting = get(state, 'event.privacySetting');

            switch (privacySetting) {
                case INVITATION_REQUIRED:
                    label = 'invite';
                    break;
                case PASSWORD_REQUIRED:
                    label = 'password';
                    break;
                default:
                    label = privacySetting
                        ? privacySetting
                        : 'missing event privacy setting';
                    break;
            }
        }

        if (startsWith(checkoutStep, GROUP_REG_JOIN_PATH)) {
            label = isUserLoggedIn(state)
                ? 'user logged in'
                : 'user logged off';
        }

        store.dispatch(
            trackPageLoad({
                checkoutStep,
                label,
            }),
        );

        trackInitialPageView(contextGASettings, {
            page,
            [DIMENSION_PARENT_URL]: widgetParentUrl,
            [DIMENSION_EVENT_ID]: eventId,
        });

        firstRun = false;
        // Reset the label so subsequent page loads don't have a residual label from a previous page load
        // e.g. status page has label "TimeExpired", but going back to the tickets page should not have any label
        label = undefined;
    });
};

/**
 * Initializes our analytics listeners.
 */
export const initializePageAnalytics = ({
    historyManagement,
    store,
    appProps,
}: {
    historyManagement: History;
    store: Store<CheckoutState>;
    appProps: any;
}) => {
    if (appProps.gaSettings && HAS_WINDOW) {
        initializeGAPageView({ historyManagement, store, appProps });
    }
};

export const getPaymentMethodLabel = (paymentMethod: PaymentMethodType) =>
    !paymentMethod ? 'change' : paymentMethod;

export const getPaymentSelectionLabel = (paymentMethod: PaymentMethodType) =>
    paymentMethod === PaymentMethodType.CREDIT
        ? 'Add new card'
        : 'Stored payment';

export const getApplePayLabel = (canMakePaymentsWithActiveCard: boolean) =>
    canMakePaymentsWithActiveCard ? 'activated' : 'set up';

export const getExpressCheckoutLabel = (step: string) => {
    if (step === TICKETS_FULL_SIZE_PATH) {
        return 'TicketSelection';
    }
    return null;
};

export const getTosCheckboxLabel = (checked: boolean) =>
    checked ? 'checked' : 'unchecked';

export const getCheckoutFormWithErrorsLabel = (
    sectionsWithErrors: Array<CheckoutFormSection>,
) =>
    sectionsWithErrors.length
        ? sectionsWithErrors
              .map((section) => CHECKOUT_FORM_SECTION_LABEL[section])
              .sort()
              .join('|')
        : '';
